import { serializeError } from "serialize-error";
import { current } from "immer";
import { createApi } from "@reduxjs/toolkit/query/react";
import { type Database, supabase, supabaseBaseQuery } from "../../../supabase";
import { promptApi } from "../../prompts/api/promptApi";
import { resourcesApi } from "../../resources/api/resourcesApi";
import type { PromptMeta, PromptTestCase } from "../../../types/Resource";
import { defaultColumnMeta } from "../constants/defaultColumnMeta";
import { getPromptVariantColor } from "../helpers/getPromptVariantColor";
import type { Eval } from "../types";

const evalsApi = createApi({
  reducerPath: "api.Evals",
  baseQuery: supabaseBaseQuery,
  tagTypes: ["Eval"],
  endpoints: (build) => ({
    createEval: build.mutation<
      string,
      {
        name: string;
        parent_id?: string | null;
        icon?: string;
      }
    >({
      queryFn: async ({ name, parent_id, icon }) => {
        const { data, error } = await supabase.rpc("create_eval", {
          p_name: name,
          p_parent_id: parent_id || undefined,
          p_icon: icon || undefined,
        });

        if (error) return { error: serializeError(error) };

        // Seed an empty draft prompt column so the new eval opens ready to edit.
        const { error: promptError } = await supabase.rpc("add_eval_prompt", {
          p_eval_id: data,
          p_prompt_meta:
            defaultColumnMeta as Database["public"]["Functions"]["add_eval_prompt"]["Args"]["p_prompt_meta"],
          p_eval_prompt_color: getPromptVariantColor(0),
        });

        if (promptError) return { error: serializeError(promptError) };

        return { data };
      },
      onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
        try {
          await queryFulfilled;
          dispatch(resourcesApi.util.invalidateTags([{ type: "Resources" }]));
        } catch {
          // Ignore errors
        }
      },
    }),

    copyEval: build.mutation<string, { evalId: string; name?: string }>({
      queryFn: async ({ evalId, name }) => {
        const { data, error } = await supabase.rpc("copy_eval", {
          p_eval_id: evalId,
          p_name: name || undefined,
        });

        if (error) return { error: serializeError(error) };

        return { data };
      },
      onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
        try {
          await queryFulfilled;
          dispatch(resourcesApi.util.invalidateTags([{ type: "Resources" }]));
        } catch {
          // Ignore errors
        }
      },
    }),

    getEval: build.query<Eval, { evalId: string }>({
      queryFn: async ({ evalId }) => {
        const { data, error } = await supabase
          .from("user_full_evals")
          .select("*")
          .eq("id", evalId)
          .single();

        if (error) return { error: serializeError(error) };

        return {
          data: {
            resource: {
              id: data.id!,
              name: data.name!,
              icon: data.icon,
              type: data.type!,
              parent_id: data.parent_id,
              sharing_status: data.sharing_status!,
              role: data.role!,
            },
            prompts: data.prompts as unknown as Eval["prompts"],
            test_cases: data.test_cases as unknown as Eval["test_cases"],
          },
        };
      },
      providesTags: (_, __, { evalId }) => [{ type: "Eval", id: evalId }],
    }),

    // Adds a new prompt column. Without `prompt_id` this creates a draft column
    // seeded from `prompt_meta`. Blank-add is fully optimistic; add-existing
    // inserts a pending skeleton and refetches to fill in committed content.
    addEvalPrompt: build.mutation<
      string,
      {
        evalId: string;
        prompt_id?: string;
        prompt_meta?: PromptMeta;
        eval_prompt_color: string;
        eval_prompt_id: string;
      }
    >({
      queryFn: async ({ evalId, prompt_id, prompt_meta, eval_prompt_color, eval_prompt_id }) => {
        const { data, error } = await supabase.rpc("add_eval_prompt", {
          p_eval_id: evalId,
          p_prompt_id: prompt_id || undefined,
          p_prompt_meta:
            prompt_meta as Database["public"]["Functions"]["add_eval_prompt"]["Args"]["p_prompt_meta"],
          p_eval_prompt_color: eval_prompt_color,
          p_eval_prompt_id: eval_prompt_id,
        });

        if (error) return { error: serializeError(error) };

        return { data };
      },
      onQueryStarted: async (
        { evalId, prompt_id, prompt_meta, eval_prompt_color, eval_prompt_id },
        { dispatch, queryFulfilled },
      ) => {
        const patch = dispatch(
          evalsApi.util.updateQueryData("getEval", { evalId }, (draft) => {
            if (prompt_id) {
              // Add-existing: committed meta is unknown until the refetch. Insert a
              // skeleton shell from known-good data only (meta unused while pending).
              draft.prompts.push({
                eval_prompt_id,
                prompt_resource_id: prompt_id,
                is_draft: false,
                disabled: false,
                dirty: false,
                color: eval_prompt_color,
                meta: defaultColumnMeta,
                pending: true,
              });
              return;
            }
            draft.prompts.push({
              eval_prompt_id,
              prompt_resource_id: null,
              is_draft: true,
              disabled: false,
              dirty: true,
              color: eval_prompt_color,
              meta: structuredClone(prompt_meta ?? defaultColumnMeta),
            });
          }),
        );
        try {
          await queryFulfilled;
        } catch {
          patch.undo();
        }
      },
      // Refetch only for add-existing to reconcile the placeholder into the real column.
      invalidatesTags: (_, error, { prompt_id, evalId }) =>
        error || !prompt_id ? [] : [{ type: "Eval", id: evalId }],
    }),

    removeEvalPrompt: build.mutation<void, { evalId: string; eval_prompt_id: string }>({
      queryFn: async ({ eval_prompt_id }) => {
        const { error } = await supabase.rpc("remove_eval_prompt", {
          p_eval_prompt_id: eval_prompt_id,
        });

        if (error) return { error: serializeError(error) };

        return { data: undefined };
      },
      onQueryStarted: async ({ evalId, eval_prompt_id }, { dispatch, queryFulfilled }) => {
        const patch = dispatch(
          evalsApi.util.updateQueryData("getEval", { evalId }, (draft) => {
            draft.prompts = draft.prompts.filter((p) => p.eval_prompt_id !== eval_prompt_id);
          }),
        );
        try {
          await queryFulfilled;
        } catch {
          patch.undo();
        }
      },
    }),

    setEvalPromptDisabled: build.mutation<
      boolean,
      { evalId: string; eval_prompt_id: string; disabled: boolean }
    >({
      queryFn: async ({ eval_prompt_id, disabled }) => {
        const { data, error } = await supabase.rpc("set_eval_prompt_disabled", {
          p_eval_prompt_id: eval_prompt_id,
          p_disabled: disabled,
        });

        if (error) return { error: serializeError(error) };

        return { data: data as boolean };
      },
      onQueryStarted: async (
        { evalId, eval_prompt_id, disabled },
        { dispatch, queryFulfilled },
      ) => {
        const patch = dispatch(
          evalsApi.util.updateQueryData("getEval", { evalId }, (draft) => {
            const col = draft.prompts.find((p) => p.eval_prompt_id === eval_prompt_id);
            if (col) col.disabled = disabled;
          }),
        );
        try {
          await queryFulfilled;
        } catch {
          patch.undo();
        }
      },
    }),

    duplicateEvalPrompt: build.mutation<
      string,
      {
        evalId: string;
        src_eval_prompt_id: string;
        new_eval_prompt_color: string;
        new_eval_prompt_id: string;
      }
    >({
      queryFn: async ({ src_eval_prompt_id, new_eval_prompt_color, new_eval_prompt_id }) => {
        const { data, error } = await supabase.rpc("duplicate_eval_prompt", {
          p_src_eval_prompt_id: src_eval_prompt_id,
          p_new_eval_prompt_color: new_eval_prompt_color,
          p_new_eval_prompt_id: new_eval_prompt_id,
        });

        if (error) return { error: serializeError(error) };

        return { data };
      },
      onQueryStarted: async (
        { evalId, src_eval_prompt_id, new_eval_prompt_color, new_eval_prompt_id },
        { dispatch, queryFulfilled },
      ) => {
        const patch = dispatch(
          evalsApi.util.updateQueryData("getEval", { evalId }, (draft) => {
            const i = draft.prompts.findIndex((p) => p.eval_prompt_id === src_eval_prompt_id);
            if (i === -1) return;
            const src = draft.prompts[i];
            // Unwrap Immer proxy before structuredClone — Proxies are not cloneable.
            draft.prompts.splice(i + 1, 0, {
              eval_prompt_id: new_eval_prompt_id,
              prompt_resource_id: null,
              is_draft: true,
              disabled: src.disabled,
              dirty: true,
              color: new_eval_prompt_color,
              meta: structuredClone(current(src.meta)),
            });
          }),
        );
        try {
          await queryFulfilled;
        } catch {
          patch.undo();
        }
      },
    }),

    moveEvalPrompt: build.mutation<
      void,
      { evalId: string; eval_prompt_id: string; direction: "left" | "right" }
    >({
      queryFn: async ({ eval_prompt_id, direction }) => {
        const { error } = await supabase.rpc("move_eval_prompt", {
          p_eval_prompt_id: eval_prompt_id,
          p_direction: direction,
        });

        if (error) return { error: serializeError(error) };

        return { data: undefined };
      },
      onQueryStarted: async (
        { evalId, eval_prompt_id, direction },
        { dispatch, queryFulfilled },
      ) => {
        const patch = dispatch(
          evalsApi.util.updateQueryData("getEval", { evalId }, (draft) => {
            const i = draft.prompts.findIndex((p) => p.eval_prompt_id === eval_prompt_id);
            const j = direction === "left" ? i - 1 : i + 1;
            if (i === -1 || j < 0 || j >= draft.prompts.length) return;
            [draft.prompts[i], draft.prompts[j]] = [draft.prompts[j], draft.prompts[i]];
          }),
        );
        try {
          await queryFulfilled;
        } catch {
          patch.undo();
        }
      },
    }),

    // Autosave a single column's working meta in place. Returns a boolean dirty
    // flag (true when the draft snapshot differs from the committed version) so
    // the client can track "Show Save changes" without refetching get_eval.
    // Intentionally does not invalidate the eval to avoid remounting on every keystroke;
    // instead patches the getEval cache so navigating away and back sees the draft.
    saveEvalPrompt: build.mutation<
      boolean,
      { evalId: string; eval_prompt_id: string; meta: PromptMeta }
    >({
      queryFn: async ({ eval_prompt_id, meta }) => {
        const { data, error } = await supabase.rpc("save_eval_prompt", {
          p_eval_prompt_id: eval_prompt_id,
          p_meta: meta as Database["public"]["Functions"]["save_eval_prompt"]["Args"]["p_meta"],
        });

        if (error) return { error: serializeError(error) };

        return { data: data as boolean };
      },
      onQueryStarted: async ({ evalId, eval_prompt_id, meta }, { dispatch, queryFulfilled }) => {
        try {
          const { data: dirty } = await queryFulfilled;
          // Clone before writing into the Immer draft — assigning the live form
          // object would autofreeze it and break subsequent RHF/Monaco edits.
          const metaClone = structuredClone(meta);
          dispatch(
            evalsApi.util.updateQueryData("getEval", { evalId }, (draft) => {
              const column = draft.prompts.find((p) => p.eval_prompt_id === eval_prompt_id);
              if (!column) return;
              column.meta = metaClone;
              column.dirty = dirty;
            }),
          );
        } catch {
          // Ignore errors — editor content remains the source of truth until remount.
        }
      },
    }),

    // Promotes a draft column to a real prompt resource (first version) or
    // commits a new version of the underlying prompt for a non-draft column.
    // Invalidates the eval (column reloads as non-draft / clean) and the
    // resource tree (promoted draft appears in the nav tree).
    saveEvalPromptAsResource: build.mutation<
      { prompt_version_id: string; resource_id: string },
      {
        evalId: string;
        eval_prompt_id: string;
        name?: string;
        version_name?: string;
        version_description?: string;
      }
    >({
      queryFn: async ({ eval_prompt_id, name, version_name, version_description }) => {
        const { data, error } = await supabase.rpc("save_eval_prompt_as_resource", {
          p_eval_prompt_id: eval_prompt_id,
          p_name: name,
          p_version_name: version_name,
          p_version_description: version_description,
        });

        if (error) return { error: serializeError(error) };

        return { data: data as { prompt_version_id: string; resource_id: string } };
      },
      invalidatesTags: (_, error, { evalId }) => (error ? [] : [{ type: "Eval", id: evalId }]),
      onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled;
          dispatch(resourcesApi.util.invalidateTags([{ type: "Resources" }]));
          if (data?.resource_id) {
            dispatch(
              promptApi.util.invalidateTags([{ type: "PromptVersions", id: data.resource_id }]),
            );
          }
        } catch {
          // Ignore errors
        }
      },
    }),

    saveEvalDataset: build.mutation<void, { evalId: string; rows: PromptTestCase[] }>({
      queryFn: async ({ evalId, rows }) => {
        const { error } = await supabase.rpc("save_eval_dataset", {
          p_eval_id: evalId,
          p_rows: rows as Database["public"]["Functions"]["save_eval_dataset"]["Args"]["p_rows"],
        });

        if (error) return { error: serializeError(error) };

        return { data: undefined };
      },
      onQueryStarted: async ({ evalId, rows }, { dispatch, queryFulfilled }) => {
        try {
          await queryFulfilled;
          const rowsClone = structuredClone(rows);
          dispatch(
            evalsApi.util.updateQueryData("getEval", { evalId }, (draft) => {
              draft.test_cases = rowsClone;
            }),
          );
        } catch {
          // Ignore errors
        }
      },
    }),
  }),
});

const {
  useCreateEvalMutation,
  useCopyEvalMutation,
  useGetEvalQuery,
  useAddEvalPromptMutation,
  useRemoveEvalPromptMutation,
  useSetEvalPromptDisabledMutation,
  useDuplicateEvalPromptMutation,
  useMoveEvalPromptMutation,
  useSaveEvalPromptMutation,
  useSaveEvalPromptAsResourceMutation,
  useSaveEvalDatasetMutation,
} = evalsApi;

export {
  evalsApi,
  useCreateEvalMutation,
  useCopyEvalMutation,
  useGetEvalQuery,
  useAddEvalPromptMutation,
  useRemoveEvalPromptMutation,
  useSetEvalPromptDisabledMutation,
  useDuplicateEvalPromptMutation,
  useMoveEvalPromptMutation,
  useSaveEvalPromptMutation,
  useSaveEvalPromptAsResourceMutation,
  useSaveEvalDatasetMutation,
};
